import { CommonModule } from '@angular/common';
import {
    Component,
    ElementRef,
    EventEmitter,
    Output,
    ViewChild,
} from '@angular/core';
import {
    FormBuilder,
    FormGroup,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { customEmailValidator } from 'app/shared/email-validator';
import { scrollToFirstInvalidControl } from 'app/shared/utils';
@Component({
    selector: 'app-policy-street-form',
    standalone: true,
    imports: [
        CommonModule,
        TranslateModule,
        ReactiveFormsModule,
        MatIconModule,
    ],
    templateUrl: './policy-street-form.component.html',
    styleUrl: './policy-street-form.component.scss',
})
export class PolicyStreetFormComponent {
    selectedLanguage: any;
    form: FormGroup;
    @Output() onSubmit: EventEmitter<any> = new EventEmitter();
    @ViewChild('formContainer') formContainer!: ElementRef;

    constructor(private fb: FormBuilder) {}

    ngOnInit() {
        this.selectedLanguage = localStorage.getItem('language');
        this.form = this.fb.group({
            name: [
                '',
                [
                    Validators.required,
                    Validators.pattern(/^[A-Za-z0-9 ]+$/), // only letters, numbers, spaces
                    Validators.maxLength(100),
                ],
            ],
            email: [
                '',
                [
                    Validators.required,
                    Validators.email, // Angular's built-in email format check
                    customEmailValidator(), // our stricter rules
                    Validators.maxLength(254),
                ],
            ],
            phoneNumber: [
                '',
                [
                    Validators.required,
                    Validators.minLength(8),
                    Validators.maxLength(10),
                ],
            ],
            companyName: [
                '',
                [
                    Validators.required,
                    Validators.pattern(/^[A-Za-z0-9&.,\- ]+$/),
                    Validators.maxLength(255),
                ],
            ],
            businessRegistrationNumber: [
                '',
                [
                    Validators.pattern(/^[A-Za-z0-9]+(-[A-Za-z0-9]+)*$/),
                    Validators.minLength(8),
                    Validators.maxLength(30),
                ],
            ],
            noOfEmployees: [
                '',
                [
                    Validators.required,
                    Validators.min(1),
                    Validators.max(99999), // value ≤ 99999
                ],
            ],
            companyAddress: [
                '',
                [
                    Validators.required,
                    Validators.pattern(/^[A-Za-z0-9&,.\-\/ ]+$/), // includes & now
                    Validators.maxLength(255),
                ],
            ],
            coverageLocation: [
                '',
                [
                    Validators.required,
                    Validators.pattern(/^[A-Za-z0-9&,.\-\/ ]+$/), // includes & now
                    Validators.minLength(10),
                    Validators.maxLength(255),
                ],
            ],
            employeeBenefitsPlan: ['', Validators.required],
            comment: [
                '',
                [
                    Validators.pattern(/^[A-Za-z0-9&.,\-\/()@#!?:;'"\s]+$/), // letters, numbers, specials
                    Validators.maxLength(1000),
                ],
            ],
            termsConditions: [false, Validators.requiredTrue],
        });
    }

    allowOnlyDigits(event: KeyboardEvent): void {
        const input = event.target as HTMLInputElement;
        const key = event.key;

        // Allow control keys
        if (
            ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'].includes(
                key
            )
        ) {
            return;
        }

        // Block non-digits
        if (!/^\d$/.test(key)) {
            event.preventDefault(); // ⬅ stops alphabets before they appear
            return;
        }

        // Prevent typing beyond 10 digits
        if (input.value.length >= 10) {
            event.preventDefault();
        }
    }

    preventNegative(event: KeyboardEvent) {
        if (event.key === '-' || event.key === 'e') {
            event.preventDefault();
        }
    }

    preventNegativeOnPaste(event: ClipboardEvent) {
        const pasteData = event.clipboardData?.getData('text');
        if (pasteData && (/[-e+]/.test(pasteData) || Number(pasteData) < 0)) {
            event.preventDefault();
        }
    }

    submitForm() {
        if (this.form.invalid) {
            this.form.markAllAsTouched();

            // find first invalid field
            const firstInvalidControl: HTMLElement | null =
                this.formContainer.nativeElement.querySelector('.ng-invalid');

            if (firstInvalidControl) {
                // scroll into view
                firstInvalidControl.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });

                // focus only if it's not a radio
                if (firstInvalidControl.getAttribute('type') !== 'radio') {
                    firstInvalidControl.focus();
                }
            }

            return;
        }

        // Prepare form data with +60 prefix for phone number
        const formData = { ...this.form.value };
        if (formData.phoneNumber && !formData.phoneNumber.startsWith('+60')) {
            formData.phoneNumber = '+60' + formData.phoneNumber;
        }

        this.onSubmit.emit(formData);
    }
}
